import * as React from "react";
import { useContext } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { useAuth } from "../hooks/useAuth";
import useHomePage from "../hooks/useHomePage";

import { ToastContext } from "./ToastProvider";

import "../App.css";

import { styled, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Typography from "@mui/material/Typography";
import Logo from "../assets/Fudopia logo_small.png";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: theme.palette.divider,
  backgroundColor: alpha(theme.palette.background.default, 0.4),
  boxShadow: theme.shadows[1],
  padding: "8px 12px",
}));

export default function AppAppBar(id) {
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const { authed, logout, setCity, cityId } = useAuth();
  const { createToast } = useContext(ToastContext);

  const { cities } = useHomePage();

  const navigate = useNavigate();

  const handleLogout = async () => {
    createToast("Logged out! See you next time.", "success", true);
    logout();
    navigate("/");
  };

  const handleCityChange = e => {
    setCity(e.target.value)
  }

  // useEffect(() => {
  //   if (!cities.length) return;
  //   const city = cities.find(c => c.id === cityId);
  //   if (!city) {
  //     setCity(cities[0].id);
  //   }
  // }, [cityId]);

  return (
    <AppBar
      position="fixed"
      sx={{
        boxShadow: 0,
        bgcolor: "#f3f8f2", // mint-creme
        borderBottom: "1px solid #192b36",
        backgroundImage: "none",
        mt: 2,
        marginTop: "0",
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar
          className="mb-navigation"
          sx={{
            boxShadow: 0,
            backgroundColor: "transparent",
            border: 0,
            padding: 0,
          }}
        >
          <Box
            sx={{ flexGrow: 1, display: "flex", alignItems: "center", px: 0 }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                flexGrow: 0,
                color: "green",
                marginRight: 3,
                alignSelf: "flex-end",
              }}
            >
              <NavLink end to="/" className="app-logo">
                <img src={Logo} alt="Fudopia logo" />
              </NavLink>
            </Typography>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <Box sx={{ p: 1 }}>
                <NavLink to="/cities">Cities</NavLink>
              </Box>              
              <Box sx={{ p: 1 }}>
                <NavLink to="/dishes">Dishes</NavLink>
              </Box>
              <Box sx={{ p: 1 }}>
                <NavLink to="/events">Events</NavLink>
              </Box>
              <Box sx={{ p: 1 }}>
                <NavLink to="/restaurants">Restaurants</NavLink>
              </Box>
              <Box sx={{ p: 1 }}>
                <NavLink to="/contact">Contact</NavLink>
              </Box>
            </Box>
          </Box>
          {cities.length && (
          <FormControl sx={{ m: 1, minWidth: 120 }}>
            <Select
              value={cityId}
              onChange={handleCityChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
            >
              {cities.map((city) => (
                <MenuItem value={city.id}>{city.title}</MenuItem>
              ))}
            </Select>
          </FormControl>    
          )}      
          {authed ? (
            <>
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="text"
                  color="info"
                  size="small"
                  sx={{ minWidth: 0 }}
                >
                  <NavLink to="dashboard">Dashboard</NavLink>
                </Button>
                <Button
                  onClick={handleLogout}
                  color="primary"
                  variant="contained"
                  size="small"
                >
                  Logout
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <NavLink to="login">
                  <Button color="primary" variant="text" size="small">
                    Login
                  </Button>
                </NavLink>

                <NavLink
                  to="register"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  <Button color="primary" variant="contained" size="small">
                    Register
                  </Button>
                </NavLink>
              </Box>
            </>
          )}

          <Box sx={{ display: { sm: "flex", md: "none" } }}>
            <IconButton aria-label="Menu button" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
            <Drawer anchor="top" open={open} onClose={toggleDrawer(false)}>
              <Box
                sx={{ p: 2, backgroundColor: "background.default" }}
                onClick={toggleDrawer(false)}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <IconButton onClick={toggleDrawer(false)}>
                    <CloseRoundedIcon />
                  </IconButton>
                </Box>
                <Divider sx={{ my: 3 }} />
                <NavLink to="/dishes">
                  <MenuItem>Dishes</MenuItem>
                </NavLink>
                <NavLink to="/events">
                  <MenuItem>Events</MenuItem>
                </NavLink>
                <NavLink to="/restaurants">
                  <MenuItem>Restaurants</MenuItem>
                </NavLink>
                <NavLink to="/cities">
                  <MenuItem>Cities</MenuItem>
                </NavLink>                
                <NavLink to="/contact">
                  <MenuItem>Contact</MenuItem>
                </NavLink>
                {authed ? (
                  <>
                    <NavLink to="dashboard">
                      <MenuItem>Dashboard</MenuItem>
                    </NavLink>
                    <MenuItem>
                      <Button
                        onClick={handleLogout}
                        color="primary"
                        variant="contained"
                        size="small"
                      >
                        Logout
                      </Button>
                    </MenuItem>
                  </>
                ) : (
                  <>
                    <NavLink to="/login">
                      <MenuItem>Login</MenuItem>
                    </NavLink>
                    <MenuItem>
                      <NavLink
                        to="register"
                        style={{ color: "white", textDecoration: "none" }}
                      >
                        <Button
                          color="primary"
                          variant="contained"
                          size="small"
                        >
                          Register
                        </Button>
                      </NavLink>
                    </MenuItem>
                  </>
                )}
              </Box>
            </Drawer>
          </Box>
        </StyledToolbar>
      </Container>
    </AppBar>
  );
}
